import { useState } from "react";
import { motion } from "framer-motion";
import { Check, RotateCcw, ShieldAlert } from "lucide-react";
import { CHECKLIST } from "@/lib/content";
import { Eyebrow, Reveal } from "./Reveal";

const Ring = ({ pct }) => {
  const c = 2 * Math.PI * 52;
  return (
    <div className="relative w-40 h-40" data-testid="hr-progress-ring">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r="52" fill="none" stroke="#DCE3D8" strokeWidth="8" />
        <motion.circle cx="60" cy="60" r="52" fill="none" stroke="#CDA34F" strokeWidth="8" strokeLinecap="round" strokeDasharray={c} animate={{ strokeDashoffset: c * (1 - pct) }} transition={{ type: "spring", stiffness: 80, damping: 18 }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-serif text-4xl text-[#154F49]" data-testid="hr-progress-percent">{Math.round(pct * 100)}%</span>
        <span className="text-xs text-[#465F57]">ready</span>
      </div>
    </div>
  );
};

export const Checklist = () => {
  const [done, setDone] = useState(() => new Set());
  const toggle = (i) => setDone((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });
  return (
    <section id="hr-requirements" className="py-20 sm:py-28 no-print" data-testid="hr-checklist-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4 space-y-6">
          <Reveal className="space-y-4">
            <Eyebrow>Before you apply</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#154F49] leading-[1.04]">The 12-item HR checklist.</h2>
            <p className="text-[#465F57] leading-relaxed">Tick off what you already have. Nothing here is uploaded or saved — it simply helps you prepare for onboarding.</p>
          </Reveal>
          <Reveal delay={0.1} className="flex items-center gap-6">
            <Ring pct={done.size / CHECKLIST.length} />
            <div className="space-y-2">
              <p className="text-sm font-semibold text-[#154F49]" data-testid="hr-progress-count">{done.size} of {CHECKLIST.length} ready</p>
              <button type="button" onClick={() => setDone(new Set())} data-testid="hr-checklist-reset" className="inline-flex items-center gap-1.5 text-sm text-[#9B742A] underline underline-offset-4"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="flex gap-3 p-4 rounded-2xl bg-[#EDF1E6] text-sm text-[#154F49]">
            <ShieldAlert className="w-5 h-5 shrink-0 text-[#9B742A]" />
            <p>HR collects copies of identity, Social Security and medical documents during onboarding. Never upload images of them through the website.</p>
          </Reveal>
        </div>
        <ol className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
          {CHECKLIST.map((item, i) => {
            const on = done.has(i);
            return (
              <Reveal key={item} delay={(i % 6) * 0.04}>
                <motion.button whileTap={{ scale: 0.98 }} type="button" onClick={() => toggle(i)} aria-pressed={on} data-testid={`hr-checklist-item-${i + 1}`}
                  className={`w-full h-full min-h-[84px] text-left flex items-start gap-4 p-5 rounded-2xl border transition-colors duration-300 ${on ? "bg-[#154F49] border-[#154F49] text-[#FBF8F1]" : "bg-white border-[#154F49]/15 text-[#1A2E2B] hover:border-[#CDA34F]"}`}>
                  <span className={`mt-0.5 w-7 h-7 rounded-full shrink-0 flex items-center justify-center border-2 transition-colors ${on ? "bg-[#CDA34F] border-[#CDA34F] text-[#154F49]" : "border-[#154F49]/30 text-transparent"}`}><Check className="w-4 h-4" /></span>
                  <span>
                    <span className={`block text-xs font-semibold tracking-widest ${on ? "text-[#E2C372]" : "text-[#9B742A]"}`}>{String(i + 1).padStart(2, "0")}</span>
                    <span className="block text-[15px] font-medium leading-snug mt-0.5">{item}</span>
                  </span>
                </motion.button>
              </Reveal>
            );
          })}
        </ol>
      </div>
    </section>
  );
};
